import { sanitizePhoneNumber, toParsablePhone } from "@/lib/utils";
import parsePhoneNumber, {
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js/max";

const DEFAULT_COUNTRY: CountryCode = "US";

export type PhoneCountryCode = CountryCode;

const PHONE_COUNTRIES = getCountries()
  .map((country) => ({
    country,
    callingCode: `+${getCountryCallingCode(country)}`,
  }))
  .sort((a, b) => a.country.localeCompare(b.country));

export const digitsOnly = sanitizePhoneNumber;

const PREFERRED_CALLING_CODE_COUNTRY: Record<string, CountryCode> = {
  "1": "US",
  "44": "GB",
};

function countryForCallingCode(callingCode: string): CountryCode | undefined {
  const preferred = PREFERRED_CALLING_CODE_COUNTRY[callingCode];
  if (preferred) return preferred;
  const matches = PHONE_COUNTRIES.filter(
    (item) => item.callingCode === `+${callingCode}`,
  );
  return matches.length === 1 ? matches[0].country : undefined;
}

function countryFromParsed(
  parsed: ReturnType<typeof parsePhoneNumber>,
): CountryCode {
  if (!parsed) return DEFAULT_COUNTRY;
  if (parsed.country) return parsed.country;
  if (parsed.countryCallingCode) {
    return countryForCallingCode(parsed.countryCallingCode) ?? DEFAULT_COUNTRY;
  }
  return DEFAULT_COUNTRY;
}

export function splitPhone(phone: string): {
  country: CountryCode;
  national: string;
} {
  const parsed = parsePhoneNumber(toParsablePhone(phone));
  if (!parsed) {
    return { country: DEFAULT_COUNTRY, national: digitsOnly(phone) };
  }
  return {
    country: countryFromParsed(parsed),
    national: parsed.nationalNumber,
  };
}

export function formatE164(
  national: string,
  country: CountryCode,
): string | null {
  const parsed = parsePhoneNumber(national, country);
  if (!parsed?.isValid() || parsed.country !== country) return null;
  return parsed.format("E.164");
}

// Bare 10-digit input is parsed as US. Re-apply the country from when editing started.
export function applyNationalPhoneEdit(
  country: CountryCode,
  editedValue: string,
): string {
  const digits = digitsOnly(editedValue);
  if (!digits) return "";

  const formatted = formatE164(digits, country);
  if (formatted) return formatted;

  const withoutTrunk = digits.replace(/^0/, "");
  if (withoutTrunk !== digits) {
    const trunkFormatted = formatE164(withoutTrunk, country);
    if (trunkFormatted) return trunkFormatted;
  }

  const significant = withoutTrunk || digits;
  return `+${getCountryCallingCode(country)}${significant}`;
}

export function toE164(value: string): string | null {
  const parsed = parsePhoneNumber(toParsablePhone(value));
  return parsed?.isValid() ? parsed.format("E.164") : null;
}

export function formatNationalPhone(phone: string): string {
  const parsed = parsePhoneNumber(toParsablePhone(phone));
  if (parsed?.isValid()) return parsed.formatNational();
  return phone;
}
